import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Decimal from 'decimal.js';
import { Receipt, ShoppingCart, Lock, Plus, Minus, Search, X, Wallet } from 'lucide-react';
import { listProducts, type Product } from '../products/products.api';
import { listSeries, type Series } from '../series/series.api';
import { createSale, type PaymentInput } from '../sales/sales.api';
import { CheckoutSheet } from '../sales/CheckoutSheet';
import { useCurrentSession } from '../cash-sessions/use-current-session';
import type { CustomerDocType, TaxCategory } from '../documents/documents.api';

type DocumentType = 'FACTURA' | 'BOLETA';

interface CartLine {
  productId: string | null;
  description: string;
  unitCode: string;
  quantity: number;
  unitPrice: number;
  taxCategory: TaxCategory;
  icbperBags: number;
  imageUrl?: string | null;
}

const IGV = 0.18;
const ICBPER_RATE = 0.5;

const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Thumb({ name, src, size = 40, radius = 10 }: { name: string; src?: string | null; size?: number; radius?: number }): JSX.Element {
  if (src) {
    return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: radius, flex: 'none' }} />;
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        fontSize: size * 0.42,
        fontWeight: 800,
        color: '#0B3A47',
        background: 'linear-gradient(135deg,#E2F8FF,#ABEFFF)',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
      }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function Stepper({ qty, onMinus, onPlus }: { qty: number; onMinus: () => void; onPlus: () => void }): JSX.Element {
  return (
    <div className="row aic" style={{ border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', height: 32 }}>
      <button
        type="button"
        onClick={onMinus}
        disabled={qty === 0}
        style={{ border: 'none', background: '#fff', width: 30, height: '100%', display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}
        aria-label="Quitar"
      >
        <Minus size={15} />
      </button>
      <div className="tnum fw7" style={{ minWidth: 30, textAlign: 'center', fontSize: 14 }}>
        {qty}
      </div>
      <button
        type="button"
        onClick={onPlus}
        style={{ border: 'none', background: '#fff', width: 30, height: '100%', display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}
        aria-label="Agregar"
      >
        <Plus size={15} />
      </button>
    </div>
  );
}

export function PosPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useCurrentSession();

  const [docType, setDocType] = useState<DocumentType>('BOLETA');
  const [docTypeCustomer, setDocTypeCustomer] = useState<CustomerDocType>('DNI');
  const [docNumber, setDocNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [seriesCode, setSeriesCode] = useState<string | undefined>(undefined);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const seriesQuery = useQuery({ queryKey: ['series'], queryFn: listSeries });
  const products = useQuery({
    queryKey: ['products', { search: productSearch }],
    queryFn: () => listProducts({ pageSize: 24, search: productSearch || undefined }),
  });

  const seriesForType: Series[] = useMemo(
    () => (seriesQuery.data ?? []).filter((s) => s.documentType === docType && s.active),
    [seriesQuery.data, docType],
  );

  const totals = useMemo(() => {
    let net = new Decimal(0);
    let igv = new Decimal(0);
    let icbper = new Decimal(0);
    for (const l of cart) {
      const lineNet = new Decimal(l.quantity).times(l.unitPrice);
      const lineIgv = l.taxCategory === 'GRAVADO' ? lineNet.times(IGV) : new Decimal(0);
      net = net.plus(lineNet);
      igv = igv.plus(lineIgv);
      icbper = icbper.plus(new Decimal(l.icbperBags ?? 0).times(ICBPER_RATE));
    }
    const total = net.plus(igv).plus(icbper);
    return {
      net: net.toFixed(2),
      igv: igv.toFixed(2),
      icbper: icbper.toFixed(2),
      total: total.toFixed(2),
      totalNum: total.toNumber(),
    };
  }, [cart]);

  const qtyInCart = (productId: string): number => cart.find((l) => l.productId === productId)?.quantity ?? 0;

  const setQty = (p: Product, q: number): void => {
    setCart((prev) => {
      if (q <= 0) return prev.filter((l) => l.productId !== p.id);
      const exists = prev.find((l) => l.productId === p.id);
      if (exists) return prev.map((l) => (l.productId === p.id ? { ...l, quantity: q } : l));
      return [
        ...prev,
        {
          productId: p.id,
          description: p.name,
          unitCode: p.unitCode,
          quantity: q,
          unitPrice: Number(p.price),
          taxCategory: p.taxCategory,
          icbperBags: 0,
          imageUrl: p.imageUrl,
        },
      ];
    });
  };

  const updateLineQty = (idx: number, q: number): void => {
    setCart((prev) => (q <= 0 ? prev.filter((_, i) => i !== idx) : prev.map((l, i) => (i === idx ? { ...l, quantity: q } : l))));
  };

  const reset = (): void => {
    setCart([]);
    setCustomerName('');
    setDocNumber('');
  };

  const sell = useMutation({
    mutationFn: (input: { payments: PaymentInput[]; paymentTerm: 'CONTADO' | 'CREDITO'; dueDate?: string }) =>
      createSale({
        lines: cart.map((l) => ({
          productId: l.productId,
          description: l.description,
          unitCode: l.unitCode,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxCategory: l.taxCategory,
          icbperBags: l.icbperBags > 0 ? l.icbperBags : undefined,
        })),
        customer: {
          docType: docTypeCustomer,
          docNumber: docNumber || (docTypeCustomer === 'DNI' ? '00000000' : ''),
          name: customerName || (docTypeCustomer === 'DNI' ? 'Cliente generico' : ''),
        },
        payments: input.payments,
        paymentTerm: input.paymentTerm,
        dueDate: input.dueDate,
        emitDocument: true,
        documentType: docType,
        seriesCode,
      }),
    onSuccess: () => {
      toast.success('Venta cobrada y comprobante emitido');
      void qc.invalidateQueries({ queryKey: ['cash-session'] });
      reset();
      setCheckoutOpen(false);
      setCartOpen(false);
      navigate('/documents');
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? (err as Error).message;
      toast.error(msg);
    },
  });

  const canCheckout =
    cart.length > 0 &&
    customerName.trim().length > 0 &&
    !sell.isPending &&
    (docType !== 'FACTURA' || docTypeCustomer === 'RUC');

  if (session.isLoading) {
    return (
      <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px' }}>
        <p className="muted t-sm">Cargando sesión de caja…</p>
      </div>
    );
  }
  if (!session.data) {
    return (
      <div className="cx cx-main" style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 32 }}>
        <div className="card card-pad fade-in" style={{ maxWidth: 420, textAlign: 'center' }}>
          <div className="empty-ico" style={{ margin: '0 auto 14px' }}>
            <Lock size={26} />
          </div>
          <div className="t-h1" style={{ marginBottom: 6 }}>
            Caja cerrada
          </div>
          <p className="muted t-sm" style={{ marginBottom: 20 }}>
            Para emitir comprobantes necesitas abrir caja primero. Declara tu efectivo inicial al inicio del turno.
          </p>
          <button className="btn btn-primary btn-block" onClick={() => navigate('/cash-sessions')}>
            Abrir caja
          </button>
        </div>
      </div>
    );
  }

  // ─── Panel "Comprobante" (carrito) ────────────────────────────────────────
  const invoicePanel = (
    <div style={{ background: '#fff', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
        <div className="row between aic" style={{ marginBottom: 12 }}>
          <div className="t-h2 fw7 row aic gap8">
            <Receipt size={18} style={{ color: 'var(--primary-ink)' }} /> Comprobante
          </div>
          <div className="seg">
            {(['BOLETA', 'FACTURA'] as const).map((t) => (
              <button
                key={t}
                className={docType === t ? 'on' : ''}
                onClick={() => {
                  setDocType(t);
                  setSeriesCode(undefined);
                  if (t === 'FACTURA') setDocTypeCustomer('RUC');
                }}
              >
                {t === 'BOLETA' ? 'Boleta' : 'Factura'}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap8" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <select
            className="select"
            value={docTypeCustomer}
            disabled={docType === 'FACTURA'}
            onChange={(e) => {
              setDocTypeCustomer(e.target.value as CustomerDocType);
              setDocNumber('');
            }}
          >
            <option value="DNI">DNI</option>
            <option value="RUC">RUC</option>
            <option value="CE">C.E.</option>
            <option value="OTROS">Otros</option>
          </select>
          <input className="input tnum" value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="N° doc." inputMode="numeric" autoComplete="off" />
          <input
            className="input"
            style={{ gridColumn: 'span 2' }}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder={docTypeCustomer === 'RUC' ? 'Razón social' : 'Nombre del cliente'}
            autoComplete="off"
          />
          <select
            className="select"
            style={{ gridColumn: 'span 2' }}
            value={seriesCode ?? '__default__'}
            onChange={(e) => setSeriesCode(e.target.value === '__default__' ? undefined : e.target.value)}
          >
            <option value="__default__">Serie automática</option>
            {seriesForType.map((s) => (
              <option key={s.id} value={s.code}>
                {s.code} {s.isDefault ? '★' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', minHeight: 120 }}>
        {cart.length === 0 ? (
          <div className="empty" style={{ padding: '60px 20px' }}>
            <div className="empty-ico">
              <ShoppingCart size={26} />
            </div>
            <div className="muted">Agrega productos al comprobante</div>
          </div>
        ) : (
          cart.map((l, i) => (
            <div key={i} className="row aic gap10" style={{ padding: '10px 8px', borderBottom: '1px solid #F4F6F9' }}>
              <Thumb name={l.description} src={l.imageUrl} size={40} />
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="fw6 t-sm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {l.description}
                </div>
                <div className="t-xs muted tnum">{fmt(l.unitPrice)} c/u</div>
              </div>
              <Stepper qty={l.quantity} onMinus={() => updateLineQty(i, l.quantity - 1)} onPlus={() => updateLineQty(i, l.quantity + 1)} />
              <div className="tnum fw7 t-sm" style={{ width: 72, textAlign: 'right' }}>
                {fmt((l.unitPrice * l.quantity).toFixed(2))}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', background: '#fff' }}>
        <div className="col gap8" style={{ marginBottom: 14 }}>
          <div className="row between t-sm">
            <span className="muted">Subtotal</span>
            <span className="tnum">{fmt(totals.net)}</span>
          </div>
          <div className="row between t-sm">
            <span className="muted" style={{ whiteSpace: 'nowrap' }}>
              IGV (18%)
            </span>
            <span className="tnum">{fmt(totals.igv)}</span>
          </div>
          {Number(totals.icbper) > 0 && (
            <div className="row between t-sm">
              <span className="muted">ICBPER</span>
              <span className="tnum">{fmt(totals.icbper)}</span>
            </div>
          )}
          <div className="row between aic" style={{ paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
            <span className="fw7" style={{ fontSize: 16 }}>
              Total
            </span>
            <span className="tnum fw8" style={{ fontSize: 22 }}>
              {fmt(totals.total)}
            </span>
          </div>
        </div>
        {docType === 'FACTURA' && docTypeCustomer !== 'RUC' && (
          <p className="t-xs" style={{ color: 'var(--error)', marginBottom: 10 }}>
            Factura requiere RUC del cliente.
          </p>
        )}
        <button
          className="btn btn-primary btn-lg btn-block"
          disabled={!canCheckout}
          onClick={() => {
            setCartOpen(false);
            setCheckoutOpen(true);
          }}
        >
          <Wallet size={19} />
          Cobrar {fmt(totals.total)}
        </button>
      </div>
    </div>
  );

  return (
    <div className="cx cx-main" style={{ height: '100%', minHeight: 0 }}>
      <div
        className="pos-grid"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 408px', height: '100%', minHeight: 0 }}
      >
        {/* LEFT — catálogo */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
          <div
            className="row aic gap12 wrap"
            style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: '#fff', minWidth: 0, rowGap: 10 }}
          >
            <div className="t-h2 fw7" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
              Punto de venta
            </div>
            <span className="badge badge-green" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
              <span className="bdot" style={{ flex: 'none' }} />
              Caja abierta · S/ {session.data.openingFloat} inicial
            </span>
            <div className="mla input-icon" style={{ flex: '1 1 220px', minWidth: 120 }}>
              <Search size={17} />
              <input className="input" placeholder="Buscar producto…" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} autoComplete="off" type="search" />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 28px', minHeight: 0 }}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))', gap: 14 }}>
              {(products.data?.items ?? []).map((p) => {
                const q = qtyInCart(p.id);
                return (
                  <div
                    key={p.id}
                    className="card"
                    style={{
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'box-shadow .14s',
                      border: q > 0 ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                    }}
                    onClick={() => setQty(p, q + 1)}
                  >
                    <div
                      style={{
                        height: 96,
                        position: 'relative',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 38,
                        fontWeight: 800,
                        color: '#0B3A47',
                        background: p.imageUrl ? undefined : 'linear-gradient(135deg,#E2F8FF,#ABEFFF)',
                      }}
                    >
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        p.name.charAt(0).toUpperCase()
                      )}
                      {q > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'var(--primary)',
                            color: '#0B3A47',
                            fontWeight: 800,
                            fontSize: 12,
                            minWidth: 22,
                            height: 22,
                            borderRadius: 99,
                            display: 'grid',
                            placeItems: 'center',
                            padding: '0 6px',
                          }}
                        >
                          {q}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px 12px' }}>
                      <div className="fw6 t-sm" style={{ lineHeight: 1.25, marginBottom: 6, minHeight: 34 }}>
                        {p.name}
                      </div>
                      <div className="row between aic">
                        <div className="tnum fw8" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
                          {fmt(p.price)}
                        </div>
                        <span className="t-xs muted">{p.unitCode === 'KGM' ? 'x kg' : 'und'}</span>
                      </div>
                      <div className="t-xs muted" style={{ marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.sku}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {!products.isLoading && (products.data?.items.length ?? 0) === 0 && (
              <div className="empty">
                <div className="empty-ico">
                  <Search size={28} />
                </div>
                <div className="muted">Sin productos. Agrégalos en el menú Productos.</div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — comprobante (desktop) */}
        <div className="pos-aside" style={{ borderLeft: '1px solid var(--border)', minHeight: 0 }}>
          {invoicePanel}
        </div>
      </div>

      {/* FAB + bottom sheet (mobile) */}
      <button
        type="button"
        className="pos-fab"
        onClick={() => setCartOpen(true)}
        disabled={cart.length === 0}
        aria-label="Ver comprobante"
        style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 16,
          zIndex: 30,
          display: 'none',
          alignItems: 'center',
          gap: 12,
          padding: '0 20px',
          height: 56,
          borderRadius: 999,
          border: 'none',
          background: 'var(--primary)',
          color: '#0B3A47',
          fontWeight: 700,
          boxShadow: '0 12px 30px -8px rgba(120,210,240,.7)',
        }}
      >
        <ShoppingCart size={20} />
        <span className="tnum">
          {cart.length > 0 ? `${cart.length} · ` : ''}
          {fmt(totals.total)}
        </span>
      </button>

      {cartOpen && (
        <>
          <div className="cx-scrim" onClick={() => setCartOpen(false)} />
          <div
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 101,
              maxHeight: '92vh',
              background: '#fff',
              borderRadius: '18px 18px 0 0',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'var(--sh-lg)',
            }}
          >
            <div className="row between aic" style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div className="t-h2 fw7">Comprobante</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setCartOpen(false)} aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>{invoicePanel}</div>
          </div>
        </>
      )}

      <CheckoutSheet
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        total={totals.totalNum}
        onConfirm={(input) => sell.mutate(input)}
        isPending={sell.isPending}
        customerHasRuc={docTypeCustomer === 'RUC'}
      />
    </div>
  );
}
